import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Star, Clock, CheckCircle2, TrendingUp, ShieldCheck, Truck, AlertCircle } from 'lucide-react';
// --- Types ---
type ListItemData = {
  id: number;
  title: string;
  content: string;
  ctaText: string;
  imagePrompt?: string;
  imageUrl?: string;
  badge?: string;
};
// --- Data ---
const listItems: ListItemData[] = [{
  id: 1,
  title: 'Finally — compression that helps trapped fluid drain',
  content: "Your legs stay swollen because trapped fluid isn't draining properly. Elivara Leggings use FlowMapping™ technology to guide that fluid out — following your body's natural drainage paths from your ankles up. It's not just squeezing. It's actually helping your legs drain the way they're supposed to.",
  ctaText: 'Feel Your Legs Drain Properly',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/jc4JJy1kT84RVRm39a344e/Ανωνυμο_σχεδιο_(60).png',
  badge: 'Drainage That Actually Works'
}, {
  id: 2,
  title: 'No more cutting, binding, or tourniquet effect',
  content: 'Forget those painful medical stockings that cut into your legs and leave marks. Elivara Leggings are soft, breathable, and comfortable enough to wear all day. No more fighting to put them on. No more painful indentations. Just gentle, effective compression that actually feels good.',
  ctaText: 'Stop Fighting With Painful Compression',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/i5Mi2JpTM9r1uAouxD8yoi/ChatGPT_Image_7_Ιαν_2026,_05_14_28_μ.μ..png',
  badge: 'Comfortable All Day Long'
}, {
  id: 3,
  title: 'Stop hiding — these don\'t scream "medical device"',
  content: "These look like nice leggings. Not medical equipment. You can wear them to work, to the gym, running errands, or out to dinner. Nobody knows you're wearing therapeutic compression. You finally get relief without looking or feeling medical.",
  ctaText: 'Get Relief Without Looking Medical',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/jZFTG2porfH5nVxsPzzEsM/ChatGPT_Image_7_Ιαν_2026,_05_14_30_μ.μ..png',
  badge: 'Looks Like Regular Activewear'
}, {
  id: 4,
  title: 'That heavy concrete feeling? It actually lifts',
  content: 'Within hours of wearing Elivara Leggings, women report feeling their legs get lighter. That awful heavy, swollen feeling that gets worse all day? It starts to ease. The constant pressure? It lifts. Your legs feel more like your legs again.',
  ctaText: 'Feel Lighter Fast',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/pNSUtT5WDmLqcdAhcwUrwm/Ανωνυμο_σχεδιο_(61).png',
  badge: 'Feel Lighter Fast'
}, {
  id: 5,
  title: 'Not adapted compression — built from scratch for lipedema',
  content: 'Most compression is made for general swelling or varicose veins. Elivara spent years studying lipedema specifically. Where fluid builds up. How sensitive the tissue is. Where pressure helps vs. where it hurts. These leggings were designed from the ground up for women with lipedema.',
  ctaText: 'Experience Lipedema-Specific Design',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/417qBqmCujiqHoS5m6TP2w/ChatGPT_Image_7_Ιαν_2026,_05_14_44_μ.μ..png',
  badge: 'Built for Lipedema Tissue'
}, {
  id: 6,
  title: "Stage 1 doesn't have to become Stage 3",
  content: 'Proper drainage can help slow lipedema progression. When trapped fluid stays in your tissue, it causes more damage over time. Elivara helps your legs drain daily, which can help prevent the worsening that comes from constant fluid buildup.',
  ctaText: 'Stop Progression Early',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/mSBUUh32aUpV7TqSqATbtg/Ανωνυμο_σχεδιο_(59).png',
  badge: 'Stop Progression Early'
}, {
  id: 7,
  title: 'Legs look better, not just feel better',
  content: 'After consistent wear, women notice visible changes. Less swelling. More shape. Less of that "column" appearance. The constant fluid buildup decreases, and your legs start to look more defined. It\'s not magic. It\'s drainage working the way it should.',
  ctaText: 'See Real Results in Weeks',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/s6Rc7CEca8PT1LCgPfYQnK/ChatGPT_Image_7_Ιαν_2026,_05_14_26_μ.μ..png',
  badge: 'Visible Results You Can See'
}, {
  id: 8,
  title: 'Save thousands on massage therapy appointments',
  content: 'MLD sessions cost $150-$200 each. Twice a week adds up to over $15,000 per year. Elivara gives you daily drainage support at home. Many women have cut their MLD appointments down to once a month or stopped completely. The leggings pay for themselves in weeks.',
  ctaText: 'Replace Expensive Sessions',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/iceKEStQQu1mwwpXRSK1GD/ChatGPT_Image_7_Ιαν_2026,_05_31_01_μ.μ..png',
  badge: 'Replace Expensive Sessions'
}, {
  id: 9,
  title: 'Graduated pressure zones that guide fluid out',
  content: "FlowMapping™ isn't just uniform squeezing. It creates specific pressure zones — strongest at your ankles where fluid pools most, gradually decreasing as it moves up. This gradient naturally encourages trapped fluid to move upward and drain properly. Like professional MLD, but built into leggings you wear daily.",
  ctaText: 'Learn How FlowMapping™ Works',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/5vvA8vZPuXoYRhodXRCS14/ChatGPT_Image_7_Ιαν_2026,_05_14_39_μ.μ..png',
  badge: 'Science-Backed Technology'
}, {
  id: 10,
  title: 'Over 3,791 women finally found relief',
  content: "The reviews speak louder than any promises. Women with lipedema who've struggled for years are finally getting consistent relief. Lighter legs. Less pain. More freedom. Over 3,791 five-star ratings from women who understand exactly what you're going through.",
  ctaText: 'Read Success Stories',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9sTMtwaPZ1hu9tXeNk68cd/ChatGPT_Image_7_Ιαν_2026,_05_15_02_μ.μ..png',
  badge: 'Proven Results'
}, {
  id: 11,
  title: 'Limited-time offer before stock runs out again',
  content: "Because of the specialized FlowMapping™ design, Elivara can only produce limited quantities. And they sell out fast. Right now, first-time customers get 40% off. But when they're gone, you'll wait weeks for the next batch. Don't miss your chance to finally get relief that actually works.",
  ctaText: 'Claim Your 40% OFF Now',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/buDgGaJAfJmzs5ci6Mqu9F/ChatGPT_Image_7_Ιαν_2026,_05_28_47_μ.μ..png',
  badge: 'Your Chance for Relief'
}];
// --- Components ---
const CountdownTimer = ({
  initialTime = 7376
}: {
  initialTime?: number;
}) => {
  const [time, setTime] = useState(initialTime);
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor(time % 3600 / 60);
  const seconds = time % 60;
  const format = (num: number) => num.toString().padStart(2, '0');
  return <div className="font-mono font-bold text-lg tracking-widest" data-id="element-2">
      {format(hours)}:{format(minutes)}:{format(seconds)}
    </div>;
};
const ImagePlaceholder = ({
  prompt,
  imageUrl
}: {
  prompt?: string;
  imageUrl?: string;
}) => {
  if (imageUrl) {
    return <div className="w-full aspect-square rounded-xl overflow-hidden shadow-md" data-id="element-3">
        <img src={imageUrl} alt={prompt || 'Elivara product image'} className="w-full h-full object-cover" data-id="element-4" />
      </div>;
  }
  return <div className="w-full aspect-square bg-gray-200 rounded-xl border-2 border-gray-300 border-dashed flex items-center justify-center p-6 text-center group relative overflow-hidden" data-id="element-5">
      <div className="absolute inset-0 bg-gray-100 opacity-50 group-hover:opacity-40 transition-opacity" data-id="element-6" />
      <span className="relative z-10 text-gray-500 font-medium text-sm italic" data-id="element-7">
        {prompt}
      </span>
      <div className="absolute bottom-3 right-3" data-id="element-8">
        <div className="bg-white/80 backdrop-blur-sm p-1.5 rounded-md shadow-sm" data-id="element-9">
          <div className="w-4 h-4 bg-gray-300 rounded-full" data-id="element-10" />
        </div>
      </div>
    </div>;
};
const ListItem = ({
  item,
  index
}: {
  item: ListItemData;
  index: number;
}) => {
  return <motion.article initial={{
    opacity: 0,
    y: 50
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.5
  }} className="flex flex-col md:flex-row gap-8 items-start py-12 border-b border-gray-100 last:border-0" data-id="element-11">
      {/* Image Column */}
      <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-12">
        <ImagePlaceholder prompt={item.imagePrompt} imageUrl={item.imageUrl} data-id="element-13" />
        {item.badge && <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-bold uppercase tracking-wide" data-id="element-14">
            <Star className="w-3 h-3 fill-orange-700" data-id="element-15" />
            {item.badge}
          </div>}
      </div>

      {/* Content Column */}
      <div className="flex-1" data-id="element-16">
        <div className="flex items-baseline gap-3 mb-3" data-id="element-17">
          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-lg shadow-sm" data-id="element-18">
            {item.id}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight" data-id="element-19">
            {item.title}
          </h2>
        </div>

        <div className="pl-11" data-id="element-20">
          <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-21">
            {item.content}
          </p>

          <a href="https://tryelivara.com/pages/elivara-landing-page" className="group inline-flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors text-base border-b-2 border-orange-100 hover:border-orange-500 pb-0.5" data-id="element-22">
            <span className="group-hover:mr-1 transition-all" data-id="element-23">
              {item.ctaText}
            </span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-24" />
          </a>
        </div>
      </div>
    </motion.article>;
};
export default function ElivaraListicle() {
  const [showMobileCTA, setShowMobileCTA] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      // Show CTA after scrolling 300px down
      setShowMobileCTA(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToOffer = () => {
    window.location.href = 'https://tryelivara.com/pages/elivara-landing-page';
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-orange-100 selection:text-orange-900" data-id="element-25">
      {/* Sticky Urgency Bar */}
      <div className="sticky top-0 z-50 bg-[#ff6b35] text-white py-3 px-4 shadow-md" data-id="element-26">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-left" data-id="element-27">
          <p className="text-sm font-medium" data-id="element-28">
            <span className="bg-white text-[#ff6b35] px-2 py-0.5 rounded text-xs font-bold mr-2" data-id="element-29">
              UPDATE
            </span>
            Elivara Compression Leggings Are Selling Out FAST! Grab Yours NOW to
            get up to 40% OFF + Fast FREE Shipping Across the US
          </p>
          <div className="hidden md:block" data-id="element-30">
            <CountdownTimer initialTime={7376} data-id="element-31" />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-id="element-32">
        {/* Header Section */}
        <header className="mb-16 text-center md:text-left" data-id="element-33">
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight" data-id="element-34">
            11 Reasons Why Women with Lipedema Are Obsessed with Elivara
            Leggings in 2026
          </h1>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded-r-lg" data-id="element-35">
            <p className="text-blue-900 font-medium" data-id="element-36">
              <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-bold mr-2 uppercase tracking-wider" data-id="element-37">
                Trending
              </span>
              Turn painful, swollen lipedema legs into lighter, more comfortable
              days — without medical compression that cuts into your skin ☁️✨
            </p>
          </div>

          <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-38">
            <p className="text-lg text-gray-700" data-id="element-39">
              <span className="font-bold text-gray-900" data-id="element-40">Summary:</span> Relief
              that actually works — FlowMapping™ compression drains trapped
              fluid, reduces swelling, and helps you move freely again. From
              your first wear, you'll feel the difference that's changing lives.
            </p>
          </div>
        </header>

        {/* List Items */}
        <div className="space-y-4" data-id="element-41">
          {listItems.map((item, index) => <ListItem key={item.id} item={item} index={index} data-id="element-42" />)}
        </div>

        {/* Transition Section */}
        <div className="my-20 text-center bg-orange-50 p-10 rounded-3xl border border-orange-100" data-id="element-43">
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-44">
            Reaching #11 means one thing: you're truly serious about finding
            relief from lipedema swelling.
          </h3>
          <p className="text-lg text-gray-600 mb-6" data-id="element-45">
            ✨ We don't share this exclusive offer every day… consider this your
            personal invitation to try what's changing lives.
          </p>
          <div className="inline-flex items-center gap-2 text-orange-600 font-bold bg-white px-4 py-2 rounded-full shadow-sm" data-id="element-46">
            <Truck className="w-5 h-5" data-id="element-47" />
            FREE SHIPPING WITH YOUR ORDER
          </div>
        </div>

        {/* Final Offer Section */}
        <section id="final-offer" className="bg-[#fff8f5] border-2 border-[#ff6b35] rounded-3xl p-6 md:p-10 shadow-xl overflow-hidden relative" data-id="element-48">
          <div className="absolute top-0 right-0 bg-[#ff6b35] text-white px-6 py-2 rounded-bl-2xl font-bold text-sm uppercase tracking-wider" data-id="element-49">
            Up to 40% OFF For a Limited Time Only!
          </div>

          <div className="flex flex-col md:flex-row gap-10 items-center mt-8" data-id="element-50">
            {/* Product Image */}
            <div className="w-full md:w-1/2" data-id="element-51">
              <div className="aspect-square bg-white rounded-2xl border border-gray-200 p-4 shadow-sm flex items-center justify-center relative overflow-hidden" data-id="element-52">
                <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded" data-id="element-53">
                  HIGH DEMAND
                </div>
                <img src="https://cdn.magicpatterns.com/uploads/wqa8bKqLEmEabd2AmxEHGu/pasted-image.png" alt="Elivara Compression Leggings showing FlowMapping pressure zones" className="w-full h-full object-contain" data-id="element-54" />
              </div>
            </div>

            {/* Offer Details */}
            <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-55">
              <h3 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-56">
                New Year, New Relief Special
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-57">
                This limited-time deal is in high demand and stock keeps selling
                out.
              </p>

              <div className="bg-white p-4 rounded-xl border border-orange-100 shadow-sm mb-6 inline-block w-full" data-id="element-58">
                <div className="flex justify-between items-center mb-2" data-id="element-59">
                  <span className="text-gray-500 text-sm font-medium" data-id="element-60">
                    DEAL ENDING IN:
                  </span>
                  <div className="text-[#ff6b35]" data-id="element-61">
                    <CountdownTimer initialTime={7376} data-id="element-62" />
                  </div>
                </div>
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden" data-id="element-63">
                  <div className="h-full bg-[#ff6b35] w-[85%] animate-pulse" data-id="element-64" />
                </div>
                <div className="flex justify-between text-xs mt-1 text-gray-400" data-id="element-65">
                  <span data-id="element-66">Stock Running Low</span>
                  <span data-id="element-67">85% Claimed</span>
                </div>
              </div>

              <a href="https://tryelivara.com/pages/elivara-landing-page" className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white text-xl font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-4 flex items-center justify-center gap-2" data-id="element-68">
                GET 40% OFF NOW
                <ArrowRight className="w-6 h-6" data-id="element-69" />
              </a>

              <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-gray-600" data-id="element-70">
                <div className="flex items-center gap-1" data-id="element-71">
                  <AlertCircle className="w-4 h-4 text-red-500" data-id="element-72" />
                  <span className="font-medium" data-id="element-73">Sell-Out Risk: High</span>
                </div>
                <div className="w-px h-4 bg-gray-300" data-id="element-74" />
                <div className="flex items-center gap-1" data-id="element-75">
                  <Truck className="w-4 h-4 text-green-600" data-id="element-76" />
                  <span className="font-medium" data-id="element-77">FREE Shipping</span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center md:justify-start gap-2 text-sm text-gray-500" data-id="element-78">
                <ShieldCheck className="w-4 h-4" data-id="element-79" />
                Try it today with a 30-Day Money-Back Guarantee!
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-20 pt-10 border-t border-gray-200 text-center text-gray-500 text-xs leading-relaxed" data-id="element-80">
          <p className="max-w-2xl mx-auto mb-8" data-id="element-81">
            This advertorial is for informational purposes only and is not
            intended to diagnose, treat, cure, or prevent any disease. Always
            consult with your healthcare provider before starting any new
            treatment regimen.
          </p>
          <div className="flex justify-center gap-6 mb-8" data-id="element-82">
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-83">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-84">
              Terms of Service
            </a>
            <a href="#" className="hover:text-gray-900 transition-colors" data-id="element-85">
              Contact Us
            </a>
          </div>
          <p data-id="element-86">&copy; 2026 Elivara. All rights reserved.</p>
        </footer>
      </main>

      {/* Sticky Mobile CTA */}
      <AnimatePresence data-id="element-87">
        {showMobileCTA && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-orange-200 shadow-2xl" data-id="element-88">
            <div className="p-4 pb-safe" data-id="element-89">
              <button onClick={scrollToOffer} className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white text-lg font-bold py-4 px-6 rounded-xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2" data-id="element-90">
                GET 40% OFF NOW
                <ArrowRight className="w-5 h-5" data-id="element-91" />
              </button>
              <p className="text-center text-xs text-gray-500 mt-2" data-id="element-92">
                Limited Time • Free Shipping
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}